import { api } from "../slices/api";
import type { PreflightResult } from "../../interfaces/Scheduling.interface";

export const schedulingApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Dashboard
    getDashboard: builder.query({ query: () => "scheduling/dashboard/", providesTags: ["Jobs"] }),

    // Staff Weekly Overview
    getStaffWeeklyOverview: builder.query({ query: (params) => ({ url: "scheduling/staff-weekly-overview/", params }), providesTags: ["StaffMembers"] }),

    // Tasks
    getTasks: builder.query({ query: (params) => ({ url: "scheduling/tasks/", params }), providesTags: ["Tasks"] }),
    getTask: builder.query({ query: (id) => `scheduling/tasks/${id}/`, providesTags: ["Tasks"] }),
    createTask: builder.mutation({ query: (body) => ({ url: "scheduling/tasks/", method: "POST", body }), invalidatesTags: ["Tasks"] }),
    updateTask: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/tasks/${id}/`, method: "PATCH", body }), invalidatesTags: ["Tasks"] }),
    deleteTask: builder.mutation({ query: (id) => ({ url: `scheduling/tasks/${id}/`, method: "DELETE" }), invalidatesTags: ["Tasks"] }),

    // Task Conditions
    getTaskConditions: builder.query({ query: (taskId) => `scheduling/tasks/${taskId}/conditions/` }),
    createTaskCondition: builder.mutation({ query: ({ taskId, ...body }) => ({ url: `scheduling/tasks/${taskId}/conditions/`, method: "POST", body }), invalidatesTags: ["Tasks"] }),
    updateTaskCondition: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/task-conditions/${id}/`, method: "PATCH", body }), invalidatesTags: ["Tasks"] }),
    deleteTaskCondition: builder.mutation({ query: (id) => ({ url: `scheduling/task-conditions/${id}/`, method: "DELETE" }), invalidatesTags: ["Tasks"] }),

    // Task Status Mappings
    getTaskStatusMappings: builder.query({ query: (params) => ({ url: "scheduling/task-status-mappings/", params }), providesTags: ["Tasks"] }),
    createTaskStatusMapping: builder.mutation({ query: (body) => ({ url: "scheduling/task-status-mappings/", method: "POST", body }), invalidatesTags: ["Tasks"] }),
    updateTaskStatusMapping: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/task-status-mappings/${id}/`, method: "PATCH", body }), invalidatesTags: ["Tasks"] }),
    deleteTaskStatusMapping: builder.mutation({ query: (id) => ({ url: `scheduling/task-status-mappings/${id}/`, method: "DELETE" }), invalidatesTags: ["Tasks"] }),

    // Staff Profiles
    getStaffProfiles: builder.query({ query: () => "scheduling/staff-profiles/", providesTags: ["StaffProfiles"] }),
    createStaffProfile: builder.mutation({ query: (body) => ({ url: "scheduling/staff-profiles/", method: "POST", body }), invalidatesTags: ["StaffProfiles"] }),
    updateStaffProfile: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/staff-profiles/${id}/`, method: "PATCH", body }), invalidatesTags: ["StaffProfiles"] }),
    deleteStaffProfile: builder.mutation({ query: (id) => ({ url: `scheduling/staff-profiles/${id}/`, method: "DELETE" }), invalidatesTags: ["StaffProfiles"] }),

    // Staff Members
    getStaffMembers: builder.query({ query: (params) => ({ url: "scheduling/staff-members/", params }), providesTags: ["StaffMembers"] }),
    getStaffMember: builder.query({ query: (id) => `scheduling/staff-members/${id}/`, providesTags: ["StaffMembers"] }),
    createStaffMember: builder.mutation({ query: (body) => ({ url: "scheduling/staff-members/", method: "POST", body }), invalidatesTags: ["StaffMembers"] }),
    updateStaffMember: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/staff-members/${id}/`, method: "PATCH", body }), invalidatesTags: ["StaffMembers"] }),
    deleteStaffMember: builder.mutation({ query: (id) => ({ url: `scheduling/staff-members/${id}/`, method: "DELETE" }), invalidatesTags: ["StaffMembers"] }),

    // Staff Schedule Weeks
    getStaffScheduleWeeks: builder.query({ query: (staffId) => `scheduling/staff-members/${staffId}/schedule-weeks/` }),
    createStaffScheduleWeek: builder.mutation({ query: ({ staffId, ...body }) => ({ url: `scheduling/staff-members/${staffId}/schedule-weeks/`, method: "POST", body }), invalidatesTags: ["StaffMembers"] }),
    bulkUpdateShiftMachines: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/staff-schedule-weeks/${id}/bulk-update-machines/`, method: "POST", body }), invalidatesTags: ["StaffMembers"] }),

    // Staff Vacations
    getStaffVacations: builder.query({ query: (staffId) => `scheduling/staff-members/${staffId}/vacations/`, providesTags: ["StaffMembers"] }),
    createStaffVacation: builder.mutation({ query: ({ staffId, ...body }) => ({ url: `scheduling/staff-members/${staffId}/vacations/`, method: "POST", body }), invalidatesTags: ["StaffMembers"] }),
    updateStaffVacation: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/staff-vacations/${id}/`, method: "PATCH", body }), invalidatesTags: ["StaffMembers"] }),
    deleteStaffVacation: builder.mutation({ query: (id) => ({ url: `scheduling/staff-vacations/${id}/`, method: "DELETE" }), invalidatesTags: ["StaffMembers"] }),

    // Staff Overtimes
    getStaffOvertimes: builder.query({ query: (staffId) => `scheduling/staff-members/${staffId}/overtimes/`, providesTags: ["StaffMembers"] }),
    createStaffOvertime: builder.mutation({ query: ({ staffId, ...body }) => ({ url: `scheduling/staff-members/${staffId}/overtimes/`, method: "POST", body }), invalidatesTags: ["StaffMembers"] }),
    updateStaffOvertime: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/staff-overtimes/${id}/`, method: "PATCH", body }), invalidatesTags: ["StaffMembers"] }),
    deleteStaffOvertime: builder.mutation({ query: (id) => ({ url: `scheduling/staff-overtimes/${id}/`, method: "DELETE" }), invalidatesTags: ["StaffMembers"] }),

    // Shifts, Blocks
    createShift: builder.mutation({ query: (body) => ({ url: "scheduling/shifts/", method: "POST", body }), invalidatesTags: ["StaffMembers"] }),
    updateShift: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/shifts/${id}/`, method: "PATCH", body }), invalidatesTags: ["StaffMembers"] }),
    deleteShift: builder.mutation({ query: (id) => ({ url: `scheduling/shifts/${id}/`, method: "DELETE" }), invalidatesTags: ["StaffMembers"] }),
    copyShiftToDays: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/shifts/${id}/copy-to-days/`, method: "POST", body }), invalidatesTags: ["StaffMembers"] }),
    createBlock: builder.mutation({ query: (body) => ({ url: "scheduling/blocks/", method: "POST", body }), invalidatesTags: ["StaffMembers"] }),
    updateBlock: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/blocks/${id}/`, method: "PATCH", body }), invalidatesTags: ["StaffMembers"] }),
    deleteBlock: builder.mutation({ query: (id) => ({ url: `scheduling/blocks/${id}/`, method: "DELETE" }), invalidatesTags: ["StaffMembers"] }),

    // Shift Day Templates
    getShiftDayTemplates: builder.query({ query: (params) => ({ url: "scheduling/shift-day-templates/", params }), providesTags: ["ShiftDayTemplates"] }),
    createShiftDayTemplate: builder.mutation({ query: (body) => ({ url: "scheduling/shift-day-templates/", method: "POST", body }), invalidatesTags: ["ShiftDayTemplates"] }),
    updateShiftDayTemplate: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/shift-day-templates/${id}/`, method: "PATCH", body }), invalidatesTags: ["ShiftDayTemplates"] }),
    deleteShiftDayTemplate: builder.mutation({ query: (id) => ({ url: `scheduling/shift-day-templates/${id}/`, method: "DELETE" }), invalidatesTags: ["ShiftDayTemplates"] }),
    applyDayTemplate: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/shift-day-templates/${id}/apply/`, method: "POST", body }), invalidatesTags: ["StaffMembers"] }),
    copyDayTemplatesFromMember: builder.mutation({ query: (body) => ({ url: "scheduling/shift-day-templates/copy-from-member/", method: "POST", body }), invalidatesTags: ["ShiftDayTemplates"] }),

    // Staff Deployed Weeks
    getStaffDeployedWeeks: builder.query({ query: (params) => ({ url: "scheduling/staff-deployed-weeks/", params }), providesTags: ["StaffMembers"] }),
    getStaffDeployedWeek: builder.query({ query: (id) => `scheduling/staff-deployed-weeks/${id}/`, providesTags: ["StaffMembers"] }),
    createStaffDeployedWeek: builder.mutation({ query: (body) => ({ url: "scheduling/staff-deployed-weeks/", method: "POST", body }), invalidatesTags: ["StaffMembers"] }),
    updateStaffDeployedWeek: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/staff-deployed-weeks/${id}/`, method: "PATCH", body }), invalidatesTags: ["StaffMembers"] }),
    deleteStaffDeployedWeek: builder.mutation({ query: (id) => ({ url: `scheduling/staff-deployed-weeks/${id}/`, method: "DELETE" }), invalidatesTags: ["StaffMembers"] }),
    bulkDeployWeeks: builder.mutation({ query: (body) => ({ url: "scheduling/staff-deployed-weeks/bulk-deploy/", method: "POST", body }), invalidatesTags: ["StaffMembers"] }),
    excludeShiftFromDeployedWeek: builder.mutation({ query: ({ id, shift_id }) => ({ url: `scheduling/staff-deployed-weeks/${id}/exclude-shift/`, method: "POST", body: { shift_id } }), invalidatesTags: ["StaffMembers"] }),
    restoreShiftToDeployedWeek: builder.mutation({ query: ({ id, shift_id }) => ({ url: `scheduling/staff-deployed-weeks/${id}/restore-shift/`, method: "POST", body: { shift_id } }), invalidatesTags: ["StaffMembers"] }),

    // Day Overrides
    getDayOverrides: builder.query({ query: (deployedWeekId) => `scheduling/staff-deployed-weeks/${deployedWeekId}/day-overrides/`, providesTags: ["StaffMembers"] }),
    setDayOverride: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/staff-deployed-weeks/${id}/set-day-override/`, method: "POST", body }), invalidatesTags: ["StaffMembers"] }),
    clearDayOverride: builder.mutation({ query: ({ id, day_index }) => ({ url: `scheduling/staff-deployed-weeks/${id}/clear-day-override/`, method: "POST", body: { day_index } }), invalidatesTags: ["StaffMembers"] }),

    // Machines
    // Renamed from getMachines: machines.ts defines the generic getMachines —
    // duplicate endpoint names on the shared api slice silently shadow each
    // other (see endpoint-names.test.ts).
    getSchedulingMachines: builder.query({ query: (params) => ({ url: "scheduling/machines/", params }), providesTags: ["Machines"] }),
    getMachine: builder.query({ query: (id) => `scheduling/machines/${id}/`, providesTags: ["Machines"] }),
    createMachine: builder.mutation({ query: (body) => ({ url: "scheduling/machines/", method: "POST", body }), invalidatesTags: ["Machines"] }),
    updateMachine: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/machines/${id}/`, method: "PATCH", body }), invalidatesTags: ["Machines"] }),
    deleteMachine: builder.mutation({ query: (id) => ({ url: `scheduling/machines/${id}/`, method: "DELETE" }), invalidatesTags: ["Machines"] }),
    // Non-blocking preflight before creating a machine: reachability (ERP→SFA→MDP/fastlane-app) + name/ip uniqueness.
    machinePreflight: builder.mutation({ query: (body) => ({ url: "scheduling/machines/preflight/", method: "POST", body }) }),
    // Per-machine cross-system presence (found in SFA / MDP / fastlane-app), via the SFA hub.
    getMachinePresence: builder.query({ query: () => "scheduling/machines/presence/" }),

    // Machine Schedule Weeks
    getMachineScheduleWeeks: builder.query({ query: (params) => ({ url: "scheduling/machine-schedule-weeks/", params }), providesTags: ["Machines"] }),
    createMachineScheduleWeek: builder.mutation({ query: (body) => ({ url: "scheduling/machine-schedule-weeks/", method: "POST", body }), invalidatesTags: ["Machines"] }),
    updateMachineScheduleWeek: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/machine-schedule-weeks/${id}/`, method: "PATCH", body }), invalidatesTags: ["Machines"] }),
    deleteMachineScheduleWeek: builder.mutation({ query: (id) => ({ url: `scheduling/machine-schedule-weeks/${id}/`, method: "DELETE" }), invalidatesTags: ["Machines"] }),

    // Machine Groups
    getMachineGroups: builder.query({ query: () => "scheduling/machine-groups/", providesTags: ["MachineGroups"] }),
    getMachineGroup: builder.query({ query: (id) => `scheduling/machine-groups/${id}/`, providesTags: ["MachineGroups"] }),
    createMachineGroup: builder.mutation({ query: (body) => ({ url: "scheduling/machine-groups/", method: "POST", body }), invalidatesTags: ["MachineGroups", "Machines"] }),
    updateMachineGroup: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/machine-groups/${id}/`, method: "PATCH", body }), invalidatesTags: ["MachineGroups", "Machines"] }),
    deleteMachineGroup: builder.mutation({ query: (id) => ({ url: `scheduling/machine-groups/${id}/`, method: "DELETE" }), invalidatesTags: ["MachineGroups", "Machines"] }),

    // Machine Downtimes
    getMachineDowntimes: builder.query({ query: (params) => ({ url: "scheduling/machine-downtimes/", params }), providesTags: ["Machines"] }),
    createMachineDowntime: builder.mutation({ query: (body) => ({ url: "scheduling/machine-downtimes/", method: "POST", body }), invalidatesTags: ["Machines"] }),
    updateMachineDowntime: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/machine-downtimes/${id}/`, method: "PATCH", body }), invalidatesTags: ["Machines"] }),
    deleteMachineDowntime: builder.mutation({ query: (id) => ({ url: `scheduling/machine-downtimes/${id}/`, method: "DELETE" }), invalidatesTags: ["Machines"] }),

    // Jobs
    getJobs: builder.query({ query: (params) => ({ url: "scheduling/jobs/", params }), providesTags: ["Jobs"] }),
    getJobsGantt: builder.query({ query: () => "scheduling/jobs/gantt/", providesTags: ["Jobs"] }),
    getJob: builder.query({ query: (id) => `scheduling/jobs/${id}/`, providesTags: ["Jobs"] }),
    createJob: builder.mutation({ query: (body) => ({ url: "scheduling/jobs/", method: "POST", body }), invalidatesTags: ["Jobs"] }),
    updateJob: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/jobs/${id}/`, method: "PATCH", body }), invalidatesTags: ["Jobs"] }),
    deleteJob: builder.mutation({ query: (id) => ({ url: `scheduling/jobs/${id}/`, method: "DELETE" }), invalidatesTags: ["Jobs"] }),
    splitJob: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/jobs/${id}/split/`, method: "POST", body }), invalidatesTags: ["Jobs", "Exceptions"] }),
    unsplitJob: builder.mutation({ query: (id) => ({ url: `scheduling/jobs/${id}/unsplit/`, method: "POST" }), invalidatesTags: ["Jobs", "Exceptions"] }),
    recalcJobMachineTime: builder.mutation({ query: (id) => ({ url: `scheduling/jobs/${id}/recalc-machine-time/`, method: "POST" }), invalidatesTags: ["Jobs"] }),

    // Job Tasks
    getJobTasks: builder.query({ query: (params) => ({ url: "scheduling/job-tasks/", params }), providesTags: ["Jobs"] }),
    updateJobTask: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/job-tasks/${id}/`, method: "PATCH", body }), invalidatesTags: ["Jobs"] }),

    // Job Variable Definitions
    getJobVariableDefinitions: builder.query({ query: () => "scheduling/job-variable-definitions/", providesTags: ["JobVarDefs"] }),
    createJobVariableDefinition: builder.mutation({ query: (body) => ({ url: "scheduling/job-variable-definitions/", method: "POST", body }), invalidatesTags: ["JobVarDefs"] }),
    deleteJobVariableDefinition: builder.mutation({ query: (id) => ({ url: `scheduling/job-variable-definitions/${id}/`, method: "DELETE" }), invalidatesTags: ["JobVarDefs"] }),

    // Job Exceptions
    getJobExceptions: builder.query({ query: (params) => ({ url: "scheduling/job-exceptions/", params }), providesTags: ["Exceptions"] }),

    // Queues
    getQueues: builder.query({ query: () => "scheduling/queues/", providesTags: ["Queues"] }),
    getQueue: builder.query({ query: (taskId) => `scheduling/queues/${taskId}/`, providesTags: ["Queues"] }),

    // Capacity
    getCapacity: builder.query({ query: (params) => ({ url: "scheduling/capacity/", params }) }),
    getCapacityAnalytics: builder.query({ query: (params) => ({ url: "scheduling/capacity/analytics/", params }) }),
    triggerHeadroom: builder.mutation({ query: () => ({ url: "scheduling/capacity/headroom/", method: "POST" }), invalidatesTags: ["Jobs", "Preflight"] }),
    cancelHeadroom: builder.mutation({ query: () => ({ url: "scheduling/capacity/headroom-cancel/", method: "POST" }), invalidatesTags: ["Jobs"] }),
    getHeadroomStatus: builder.query({ query: () => "scheduling/capacity/headroom-status/", providesTags: ["Jobs"] }),

    // Scheduling
    runScheduling: builder.mutation({ query: () => ({ url: "scheduling/run/", method: "POST" }), invalidatesTags: ["Jobs", "Queues", "Exceptions", "ScheduleRuns", "Preflight"] }),
    runCPScheduling: builder.mutation({ query: (body) => ({ url: "scheduling/run-cp/", method: "POST", body }), invalidatesTags: ["Jobs", "Queues", "Exceptions", "ScheduleRuns", "Preflight"] }),
    getEngineStatus: builder.query({ query: () => "scheduling/engine-status/", providesTags: ["ScheduleRuns"] }),
    getPreflight: builder.query<PreflightResult, { skip?: string } | void>({
      query: (params) => ({
        url: "scheduling/preflight/",
        params: params || undefined,
      }),
      providesTags: ["Preflight"],
    }),

    // Schedule Runs
    getScheduleRuns: builder.query({ query: () => "scheduling/schedule-runs/", providesTags: ["ScheduleRuns"] }),
    // Failed runs completed in the last `withinMinutes`, newest first, slim (with error_message).
    // What the Dashboard's failure banner polls: the full list above is every run ever with its
    // solver_stats, ~14 MB of JSON (PTB-6921).
    getRecentScheduleRunFailures: builder.query({
      query: (withinMinutes: number = 60) => ({
        url: "scheduling/schedule-runs/recent-failures/",
        params: { within_minutes: withinMinutes },
      }),
      providesTags: ["ScheduleRuns"],
    }),
    getScheduleRunDetail: builder.query({ query: (id) => `scheduling/schedule-runs/${id}/`, providesTags: ["ScheduleRuns"] }),
    compareScheduleRuns: builder.query({ query: ({ runA, runB }) => `scheduling/schedule-runs/compare/?run_a=${runA}&run_b=${runB}` }),
    validateSchedule: builder.query<any, { job?: string; check?: string } | void>({
      query: (params) => ({
        url: "scheduling/schedule-runs/validate/",
        params: params || undefined,
      }),
    }),

    // Settings
    getHolidays: builder.query({ query: () => "scheduling/holidays/", providesTags: ["Holidays"] }),
    createHoliday: builder.mutation({ query: (body) => ({ url: "scheduling/holidays/", method: "POST", body }), invalidatesTags: ["Holidays"] }),
    updateHoliday: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/holidays/${id}/`, method: "PATCH", body }), invalidatesTags: ["Holidays"] }),
    deleteHoliday: builder.mutation({ query: (id) => ({ url: `scheduling/holidays/${id}/`, method: "DELETE" }), invalidatesTags: ["Holidays"] }),

    getBusinessHours: builder.query({ query: () => "scheduling/business-hours/", providesTags: ["BusinessHours"] }),
    updateBusinessHours: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/business-hours/${id}/`, method: "PATCH", body }), invalidatesTags: ["BusinessHours"] }),

    getFacilitySettings: builder.query({ query: () => "scheduling/facility-settings/", providesTags: ["FacilitySettings"] }),
    updateFacilitySettings: builder.mutation({ query: (body) => ({ url: "scheduling/facility-settings/1/", method: "PATCH", body }), invalidatesTags: ["FacilitySettings"] }),

    getRoles: builder.query({ query: () => "scheduling/roles/", providesTags: ["Roles"] }),
    createRole: builder.mutation({ query: (body) => ({ url: "scheduling/roles/", method: "POST", body }), invalidatesTags: ["Roles"] }),
    updateRole: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/roles/${id}/`, method: "PATCH", body }), invalidatesTags: ["Roles"] }),
    deleteRole: builder.mutation({ query: (id) => ({ url: `scheduling/roles/${id}/`, method: "DELETE" }), invalidatesTags: ["Roles"] }),

    getAuditLog: builder.query({ query: (params) => ({ url: "scheduling/audit-log/", params }) }),

    // Phantom Job Templates & Headroom Iteration Rules
    getPhantomTemplates: builder.query({ query: () => "scheduling/phantom-templates/", providesTags: ["PhantomTemplates"] }),
    getPhantomTemplate: builder.query({ query: (id) => `scheduling/phantom-templates/${id}/`, providesTags: ["PhantomTemplates"] }),
    createPhantomTemplate: builder.mutation({ query: (body) => ({ url: "scheduling/phantom-templates/", method: "POST", body }), invalidatesTags: ["PhantomTemplates"] }),
    updatePhantomTemplate: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/phantom-templates/${id}/`, method: "PATCH", body }), invalidatesTags: ["PhantomTemplates"] }),
    deletePhantomTemplate: builder.mutation({ query: (id) => ({ url: `scheduling/phantom-templates/${id}/`, method: "DELETE" }), invalidatesTags: ["PhantomTemplates"] }),

    // Affinity Configuration
    getAffinityConfig: builder.query({ query: () => "scheduling/affinity-config/", providesTags: ["AffinityConfig"] }),
    updateAffinityConfig: builder.mutation({ query: (body) => ({ url: "scheduling/affinity-config/1/", method: "PATCH", body }), invalidatesTags: ["AffinityConfig"] }),

    // Prioritization Tables
    getPrioritizationTables: builder.query({ query: () => "scheduling/prioritization-tables/", providesTags: ["PrioritizationTables"] }),
    getPrioritizationTable: builder.query({ query: (id: number) => `scheduling/prioritization-tables/${id}/`, providesTags: ["PrioritizationTables"] }),
    createPrioritizationTable: builder.mutation({ query: (body) => ({ url: "scheduling/prioritization-tables/", method: "POST", body }), invalidatesTags: ["PrioritizationTables"] }),
    updatePrioritizationTable: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/prioritization-tables/${id}/`, method: "PATCH", body }), invalidatesTags: ["PrioritizationTables"] }),
    deletePrioritizationTable: builder.mutation({ query: (id: number) => ({ url: `scheduling/prioritization-tables/${id}/`, method: "DELETE" }), invalidatesTags: ["PrioritizationTables"] }),

    // Prioritization Table Entries
    createPrioritizationEntry: builder.mutation({ query: (body) => ({ url: "scheduling/prioritization-entries/", method: "POST", body }), invalidatesTags: ["PrioritizationTables"] }),
    updatePrioritizationEntry: builder.mutation({ query: ({ id, ...body }) => ({ url: `scheduling/prioritization-entries/${id}/`, method: "PATCH", body }), invalidatesTags: ["PrioritizationTables"] }),
    deletePrioritizationEntry: builder.mutation({ query: (id: number) => ({ url: `scheduling/prioritization-entries/${id}/`, method: "DELETE" }), invalidatesTags: ["PrioritizationTables"] }),
  }),
});

export const {
  useGetDashboardQuery,
  useGetStaffWeeklyOverviewQuery,
  useGetTasksQuery,
  useGetTaskQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useGetTaskConditionsQuery,
  useCreateTaskConditionMutation,
  useUpdateTaskConditionMutation,
  useDeleteTaskConditionMutation,
  useGetTaskStatusMappingsQuery,
  useCreateTaskStatusMappingMutation,
  useUpdateTaskStatusMappingMutation,
  useDeleteTaskStatusMappingMutation,
  useGetStaffProfilesQuery,
  useCreateStaffProfileMutation,
  useUpdateStaffProfileMutation,
  useDeleteStaffProfileMutation,
  useGetStaffMembersQuery,
  useGetStaffMemberQuery,
  useCreateStaffMemberMutation,
  useUpdateStaffMemberMutation,
  useDeleteStaffMemberMutation,
  useGetStaffScheduleWeeksQuery,
  useCreateStaffScheduleWeekMutation,
  useBulkUpdateShiftMachinesMutation,
  useGetStaffVacationsQuery,
  useCreateStaffVacationMutation,
  useUpdateStaffVacationMutation,
  useDeleteStaffVacationMutation,
  useGetStaffOvertimesQuery,
  useCreateStaffOvertimeMutation,
  useUpdateStaffOvertimeMutation,
  useDeleteStaffOvertimeMutation,
  useCreateShiftMutation,
  useUpdateShiftMutation,
  useDeleteShiftMutation,
  useCopyShiftToDaysMutation,
  useCreateBlockMutation,
  useUpdateBlockMutation,
  useDeleteBlockMutation,
  useGetShiftDayTemplatesQuery,
  useCreateShiftDayTemplateMutation,
  useUpdateShiftDayTemplateMutation,
  useDeleteShiftDayTemplateMutation,
  useApplyDayTemplateMutation,
  useCopyDayTemplatesFromMemberMutation,
  useGetStaffDeployedWeeksQuery,
  useGetStaffDeployedWeekQuery,
  useCreateStaffDeployedWeekMutation,
  useUpdateStaffDeployedWeekMutation,
  useDeleteStaffDeployedWeekMutation,
  useBulkDeployWeeksMutation,
  useExcludeShiftFromDeployedWeekMutation,
  useRestoreShiftToDeployedWeekMutation,
  useGetDayOverridesQuery,
  useSetDayOverrideMutation,
  useClearDayOverrideMutation,
  useGetSchedulingMachinesQuery,
  useGetMachineQuery,
  useCreateMachineMutation,
  useUpdateMachineMutation,
  useDeleteMachineMutation,
  useMachinePreflightMutation,
  useGetMachinePresenceQuery,
  useGetMachineGroupsQuery,
  useGetMachineGroupQuery,
  useCreateMachineGroupMutation,
  useUpdateMachineGroupMutation,
  useDeleteMachineGroupMutation,
  useGetMachineScheduleWeeksQuery,
  useCreateMachineScheduleWeekMutation,
  useUpdateMachineScheduleWeekMutation,
  useDeleteMachineScheduleWeekMutation,
  useGetMachineDowntimesQuery,
  useCreateMachineDowntimeMutation,
  useUpdateMachineDowntimeMutation,
  useDeleteMachineDowntimeMutation,
  useGetJobsQuery,
  useGetJobsGanttQuery,
  useGetJobQuery,
  useCreateJobMutation,
  useUpdateJobMutation,
  useDeleteJobMutation,
  useSplitJobMutation,
  useUnsplitJobMutation,
  useRecalcJobMachineTimeMutation,
  useGetJobTasksQuery,
  useUpdateJobTaskMutation,
  useGetJobVariableDefinitionsQuery,
  useCreateJobVariableDefinitionMutation,
  useDeleteJobVariableDefinitionMutation,
  useGetJobExceptionsQuery,
  useGetQueuesQuery,
  useGetQueueQuery,
  useGetCapacityQuery,
  useGetCapacityAnalyticsQuery,
  useTriggerHeadroomMutation,
  useCancelHeadroomMutation,
  useGetHeadroomStatusQuery,
  useRunSchedulingMutation,
  useRunCPSchedulingMutation,
  useGetEngineStatusQuery,
  useGetPreflightQuery,
  useLazyGetPreflightQuery,
  useGetScheduleRunsQuery,
  useGetRecentScheduleRunFailuresQuery,
  useGetScheduleRunDetailQuery,
  useCompareScheduleRunsQuery,
  useLazyValidateScheduleQuery,
  useGetHolidaysQuery,
  useCreateHolidayMutation,
  useUpdateHolidayMutation,
  useDeleteHolidayMutation,
  useGetBusinessHoursQuery,
  useUpdateBusinessHoursMutation,
  useGetFacilitySettingsQuery,
  useUpdateFacilitySettingsMutation,
  useGetRolesQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useGetAuditLogQuery,
  useGetPhantomTemplatesQuery,
  useGetPhantomTemplateQuery,
  useCreatePhantomTemplateMutation,
  useUpdatePhantomTemplateMutation,
  useDeletePhantomTemplateMutation,
  useGetAffinityConfigQuery,
  useUpdateAffinityConfigMutation,
  useGetPrioritizationTablesQuery,
  useGetPrioritizationTableQuery,
  useCreatePrioritizationTableMutation,
  useUpdatePrioritizationTableMutation,
  useDeletePrioritizationTableMutation,
  useCreatePrioritizationEntryMutation,
  useUpdatePrioritizationEntryMutation,
  useDeletePrioritizationEntryMutation,
} = schedulingApi;
